import type {ReactElement} from 'react';
import {useLayoutEffect, useState} from 'react';
import {useAppSelector} from '@/hooks';
import type {CustomItemControlPositions} from '@Components/poster-editor/components/custom-item-controls/components/single-item-custom-controls/single-item-custom-controls.types';
import {getRotatedControlAnchor} from '@Components/poster-editor/components/custom-item-controls/custom-item-control-anchor.util';
import {BaseCustomItemControl} from '@Components/poster-editor/components/custom-item-controls/components/base-custom-item-control/base-custom-item-control';
import {ControlType} from '@Components/poster-editor/components/custom-item-controls/components/base-custom-item-control/base-custom-item-control.types';
import type {CustomItemControlsProps} from '@Components/poster-editor/components/custom-item-controls/components/custom-item-controls-wrapper/custom-item-controls-wrapper.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {isUserPremium} from '@Libraries/user.library';
import {openGettyWatermarkUpsellModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {
  CUSTOM_REMOVE_WATERMARK_CONTROL_ID,
  CUSTOM_REMOVE_WATERMARK_CONTROL_MARGIN,
  REMOVE_WATERMARK_CONTROL_BORDER_RADIUS,
  REMOVE_WATERMARK_CONTROL_ESTIMATED_HEIGHT,
  REMOVE_WATERMARK_CONTROL_ESTIMATED_WIDTH,
} from './custom-remove-watermark-control.types';
import type {RemoveWatermarkControlSize} from './custom-remove-watermark-control.types';
import './custom-remove-watermark-control.module.scss';

/**
 * The pill is `fit-content` around a translated label, so only the DOM knows how wide it really is — the
 * estimate below is far too narrow for e.g. German "Wasserzeichen entfernen". Cached at module scope because
 * the label cannot change within a session: only the first selection pays for a measurement, and every later
 * one is positioned from the real size on its first render.
 */
let measuredControlSize: RemoveWatermarkControlSize | null = null;

const ESTIMATED_CONTROL_SIZE: RemoveWatermarkControlSize = {
  width: REMOVE_WATERMARK_CONTROL_ESTIMATED_WIDTH,
  height: REMOVE_WATERMARK_CONTROL_ESTIMATED_HEIGHT,
};

export function CustomRemoveWatermarkControl(props: CustomItemControlsProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId(props.pageHashedId);
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const [controlSize, setControlSize] = useState<RemoveWatermarkControlSize>(measuredControlSize ?? ESTIMATED_CONTROL_SIZE);

  const arePosterItemsMoving = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsMoving[props.pageHashedId];
  });
  const arePosterItemsScaling = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsScaling[props.pageHashedId];
  });
  const arePosterItemsRotating = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsRotating[props.pageHashedId];
  });

  // Measures once per session, before paint, so the first render's estimated position is corrected without a
  // visible jump and no later render pays for a layout read. offsetWidth/Height rather than a bounding rect:
  // those report the pill's own box even if it is ever rendered rotated.
  useLayoutEffect((): void => {
    if (measuredControlSize) {
      return;
    }
    const controlElement = document.getElementById(CUSTOM_REMOVE_WATERMARK_CONTROL_ID);
    if (!controlElement || controlElement.offsetWidth === 0) {
      return;
    }
    measuredControlSize = {width: controlElement.offsetWidth, height: controlElement.offsetHeight};
    setControlSize(measuredControlSize);
  });

  // isPremium() is the watermark's own predicate — non-purchased Getty image, extracted Getty sticker or Getty
  // video, and not a removed background — so the pill appears exactly where a watermark is actually burned in.
  if (!activeItemId || !activeItem || isUserPremium() || !activeItem.isPremium()) {
    return null;
  }

  const itemOnScreenWidth = props.positionProperties.scaledWidth * props.positionProperties.posterScale;
  const itemOnScreenHeight = props.positionProperties.scaledHeight * props.positionProperties.posterScale;
  const isItemSmallerThanControl =
    itemOnScreenWidth < controlSize.width + CUSTOM_REMOVE_WATERMARK_CONTROL_MARGIN * 2 || itemOnScreenHeight < controlSize.height + CUSTOM_REMOVE_WATERMARK_CONTROL_MARGIN * 2;

  /**
   * Tucked inside the item's top-right corner and turned with the item, so it sits parallel to the item's top
   * edge at any angle. Because it shares the item's axes, half its width and height are exactly how far it
   * reaches toward each edge — the inset is the margin plus that, with no projection to approximate.
   */
  const getRemoveWatermarkButtonPosition = (): CustomItemControlPositions => {
    return getRotatedControlAnchor({
      positionProperties: props.positionProperties,
      controlWidth: controlSize.width,
      controlHeight: controlSize.height,
      controlAngleDegrees: props.positionProperties.angle,
      offsetOnItemRight: itemOnScreenWidth / 2 - CUSTOM_REMOVE_WATERMARK_CONTROL_MARGIN - controlSize.width / 2,
      offsetOnItemDown: -(itemOnScreenHeight / 2 - CUSTOM_REMOVE_WATERMARK_CONTROL_MARGIN - controlSize.height / 2),
    });
  };

  const onRemoveWatermarkClicked = (): void => {
    trackPosterBuilderGA4Events(GA4EventName.GETTY_REMOVE_WATERMARK_CLICKED);
    openGettyWatermarkUpsellModal();
  };

  const getRemoveWatermarkButton = (): ReactElement => {
    const position = getRemoveWatermarkButtonPosition();

    return (
      <BaseCustomItemControl
        icon={'icon-crown'}
        text={window.i18next.t('pmwjs_remove_watermark')}
        controlId={CUSTOM_REMOVE_WATERMARK_CONTROL_ID}
        top={position.top}
        left={position.left}
        angle={position.angle}
        borderRadius={REMOVE_WATERMARK_CONTROL_BORDER_RADIUS}
        isVisible
        onPointerDown={onRemoveWatermarkClicked}
        controlType={ControlType.CLICK}
      />
    );
  };

  if (arePosterItemsMoving || arePosterItemsRotating || arePosterItemsScaling || isItemSmallerThanControl) {
    return null;
  }

  return getRemoveWatermarkButton();
}
